import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { registerLicense } from '@syncfusion/ej2-base';
import { Auth0Provider } from '@auth0/auth0-react';
// Global 402 "out of AI tokens" handling for raw-axios service calls (side effect).
import './utils/globalAxiosTokenInterceptor';
// Import toastr CSS globally
import 'toastr/build/toastr.min.css';
import { rememberInviteFromUrl } from './Components/PendingInviteClaimer';

// An adviser-network invitation link (/network?invite=… or ?subcontract=…) is often opened by someone who has no
// account yet: keep its token through sign-up and onboarding, then PendingInviteClaimer attaches it to the account.
rememberInviteFromUrl();

// Suppress ResizeObserver loop error (benign warning from ReactFlow and other resize-based libraries)
// This error is harmless and occurs when resize observations exceed the animation frame budget
const suppressResizeObserverError = () => {
  const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
  const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
  if (resizeObserverErr) {
    resizeObserverErr.setAttribute('style', 'display: none');
  }
  if (resizeObserverErrDiv) {
    resizeObserverErrDiv.setAttribute('style', 'display: none');
  }
};

// Handle the error event
window.addEventListener('error', (e) => {
  if (e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
      e.message === 'ResizeObserver loop limit exceeded') {
    e.stopImmediatePropagation();
    e.stopPropagation();
    e.preventDefault();
    suppressResizeObserverError();
  }
});

// Also suppress via ResizeObserver polyfill approach
const debounce = (callback, delay) => {
  let tid;
  return function (...args) {
    const ctx = this;
    tid && clearTimeout(tid);
    tid = setTimeout(() => {
      callback.apply(ctx, args);
    }, delay);
  };
};

const _ = window.ResizeObserver;
window.ResizeObserver = class ResizeObserver extends _ {
  constructor(callback) {
    callback = debounce(callback, 20);
    super(callback);
  }
};

// Register Syncfusion license (make sure to replace with your actual license key)
registerLicense("Ngo9BigBOggjHTQxAR8/V1NCaF1cXGdCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdnWXZfdnVQQmNeVEF1XEQ=");

// Auth0 provider that restores the intended deep-link after login. Must live INSIDE
// <BrowserRouter> so it can use React Router's navigate. Without an onRedirectCallback
// the SDK only replaceState()s to returnTo, which React Router ignores — so every
// post-login load rendered "/" (the dashboard), losing deep links like /network.
const Auth0ProviderWithNavigate = ({ children }) => {
  const navigate = useNavigate();
  const onRedirectCallback = (appState) => {
    navigate(appState?.returnTo || window.location.pathname, { replace: true });
  };
  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
      onRedirectCallback={onRedirectCallback}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: "ImavenAPI",
        scope: "openid profile email"
      }}
    >
      {children}
    </Auth0Provider>
  );
};

// Create the root element for React
const root = ReactDOM.createRoot(document.getElementById('root'));

// Define the main element containing the app
const elem = (
  <div>
    {/* Load External CSS */}
    {/* <link href="https://cdn.syncfusion.com/ej2/27.1.48/fluent2.css" rel="stylesheet" /> */}
    <link href="https://cdn.syncfusion.com/ej2/24.1.41/material3.css" rel="stylesheet" />
    <link href="https://maxcdn.bootstrapcdn.com/bootstrap/3.3.7/css/bootstrap.min.css" rel="stylesheet" />

    {/* Configure Browser Router and Auth0 Provider */}
    <BrowserRouter>
      <Auth0ProviderWithNavigate>
        {/* Main Application Component */}
        <App />
      </Auth0ProviderWithNavigate>
    </BrowserRouter>
  </div>
);

// Render the main element into the root
root.render(elem);

// Log web vitals for performance measurement (optional)
reportWebVitals();
